import { AutocompleteOption } from "components/Form/components/Autocomplete/Autocomplete";
import { useInfiniteScroll } from "components/Form/components/Autocomplete/useInfiniteScroll";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { ProvisioningStateDot } from "components/ProvisioningStateDot/ProvisioningStateDot";
import Tooltip from "components/Tooltip/Tooltip";
import { debounce } from "lodash";
import { MdiReactIconComponentType } from "mdi-react";
import {
    Children,
    PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Link } from "react-router-dom";
import { components, OptionProps, SingleValueProps, ValueContainerProps } from "react-select";
import { VariableSizeList as List } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import "./ReactSelectComponents.scss";

export interface ResourceOption {
    value: string | null;
    label: string;
    icon?: string | null;
    link?: string | null;
    is_readonly?: boolean;
    iconName?: string;
}

const getOptionIcon = ({ icon, iconReplacement, iconName }) =>
    icon ? (
        <img className="icon" alt="" src={icon} />
    ) : iconReplacement || iconName ? (
        <span className="icon">
            <Icon icon={iconReplacement || IconCollection[iconName]} />
        </span>
    ) : null;

// generates a list of options with icons besides the entries for react-select
export const IconOptionGenerator = (
    defaultValue: any,
    noIcons: boolean,
    iconReplacement?: MdiReactIconComponentType
) => {
    // return ({ data, ...props }: { data: any; props: OptionProps<ResourceOption> }) => {
    return (props: OptionProps<ResourceOption>) => {
        // override types here because we know that showReadOnlyChip is a valid prop
        const showReadOnlyChip = (props.selectProps as any)?.showReadOnlyChip;

        const defaultValueClass = props.data.value === defaultValue?.value ? " defaultOption" : "";

        const icon = getOptionIcon({
            icon: props.data?.icon,
            iconReplacement,
            iconName: props.data?.iconName,
        });

        return (
            components.Option && (
                <components.Option className={defaultValueClass} {...props}>
                    {props.data.value ? <ProvisioningStateDot id={props.data.value} /> : null}
                    {icon ?? (noIcons && <div className="icon"></div>)}

                    <span className="label">{props.data.label}</span>

                    {showReadOnlyChip && props.data?.is_readonly && (
                        <Tooltip
                            overlay="read-only"
                            overlayStyle={{ zIndex: 20000 }}
                            placement="right"
                        >
                            <span className="readOnlyIcon">
                                <Icon icon={IconCollection.lock} />
                            </span>
                        </Tooltip>
                    )}
                </components.Option>
            )
        );
    };
};

// our custom clear indicator
export const ClearIndicator = (props: OptionProps) =>
    components.ClearIndicator && (
        <components.ClearIndicator {...props}>
            <Icon icon={IconCollection.close} />
        </components.ClearIndicator>
    );

// our custom dropdown indicator
export const DropdownIndicator = (props: OptionProps) =>
    components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
            <Icon icon={IconCollection.chevronDown} />
        </components.DropdownIndicator>
    );

export const SingleValueInner = ({
    data,
    iconReplacement,
}: {
    data: ResourceOption;
    iconReplacement?: MdiReactIconComponentType;
}) => {
    const icon = getOptionIcon({
        icon: data.icon,
        iconReplacement,
        iconName: data.iconName,
    });

    return (
        <>
            <div className="singleValue__entry">
                {data.value ? <ProvisioningStateDot id={data.value} /> : null}
                {icon}
                <span className="singleValue__label">{data?.label}</span>
            </div>
            {data?.link && (
                <Link
                    to={data?.link}
                    className="singleValue__link"
                    // prevent the dropdown from toggling when clicking the link
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    <Icon className="singleValue__link_icon" icon={IconCollection.openInNew} />
                </Link>
            )}
        </>
    );
};

// our custom single value component with a link to the record inside the autocomplete field
export const SingleValue = ({ children, ...props }: SingleValueProps<ResourceOption>) => {
    return (
        <components.SingleValue {...props} className="singleValueContainer">
            <SingleValueInner data={props.data} iconReplacement={(props as any).iconReplacement} />
        </components.SingleValue>
    );
};

/** Stable react-select component; reads `iconReplacement` from selectProps. */
export const SingleValueWithIconReplacement = (props: SingleValueProps<ResourceOption>) => (
    <SingleValue {...props} iconReplacement={(props.selectProps as any)?.iconReplacement} />
);

export type AutocompleteMenuListExtras = {
    hasMoreData?: boolean;
    hasInfiniteLoader?: boolean;
    isDataLoading?: boolean;
    loadMore?: () => void | Promise<void>;
    currentScrollPosition?: number;
    setCurrentScrollPosition?: (position: number) => void;
    isSelectedOptionHidden?: boolean;
    selectedValue?: any;
};

/** Stable MenuList wrapper for searchable autocompletes; extras come from selectProps. */
export const AutocompleteSearchMenuList = (props: any) => {
    const extras: AutocompleteMenuListExtras =
        props.selectProps?.autocompleteMenuListExtras ?? {};
    return (
        <MenuList
            {...props}
            hasMoreData={extras.hasMoreData}
            hasInfiniteLoader={extras.hasInfiniteLoader}
            isDataLoading={extras.isDataLoading}
            loadMore={extras.loadMore}
            currentScrollPosition={extras.currentScrollPosition}
            setCurrentScrollPosition={extras.setCurrentScrollPosition}
            isSelectedOptionHidden={extras.isSelectedOptionHidden}
            selectedValue={extras.selectedValue}
        />
    );
};

/** Stable MenuList for static searchable selects (no infinite loader). */
export const StaticSearchMenuList = (props: any) => (
    <MenuList {...props} hasInfiniteLoader={false} />
);

// our custom no options message. literally just shows "No options"
export const NoOptionsMessage = () => <span className="noOptionsMessage">No options</span>;

// our custom loading message. literally just shows "Loading..."
export const LoadingMessage = (props: OptionProps) =>
    components.LoadingMessage && (
        <components.LoadingMessage {...props}>
            <span className="loadingMessage">Loading...</span>
        </components.LoadingMessage>
    );

export const ValueContainer = ({ children, ...props }: ValueContainerProps) => (
    <components.ValueContainer {...props}>
        <Icon icon={IconCollection.magnify} />
        {children}
    </components.ValueContainer>
);

export const Dropdown = ({ children, target, targetClassName, targetStyle }: any) => (
    <div
        className={`input__field react-select__dropdown ${targetClassName ?? ""}`}
        style={targetStyle}
    >
        {target}
        {children}
    </div>
);

export const GroupLabel = ({ label }: { label: string }) => (
    <div className="groupLabel">{label}</div>
);

export const OptionLabel = ({
    option,
    showReadOnlyChip,
    defaultValue,
    iconReplacement,
    removeOption,
}: {
    option: AutocompleteOption;
    showReadOnlyChip: boolean;
    defaultValue?: AutocompleteOption;
    iconReplacement?: MdiReactIconComponentType;
    removeOption?: (e) => void;
}) => {
    const defaultValueClass = option.value === defaultValue?.value ? "defaultOption" : "";

    const icon = getOptionIcon({
        icon: option.icon,
        iconReplacement,
        iconName: option.iconName,
    });

    return (
        <div
            style={{ display: "flex", justifyContent: "space-between", width: "100%" }}
            data-testid="react-select__option"
        >
            <div
                className={defaultValueClass}
                style={{
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                    width: `${removeOption ? "calc(100% - 30px)" : "100%"}`,
                }}
            >
                {option.value ? <ProvisioningStateDot id={option.value} /> : null}
                {icon}

                <span className="label" style={{ width: "100%", wordBreak: "break-word" }}>
                    {option.label}
                </span>

                {showReadOnlyChip && option?.is_readonly && (
                    <Tooltip overlay="read-only" overlayStyle={{ zIndex: 20000 }} placement="right">
                        <span className="readOnlyIcon">
                            <Icon icon={IconCollection.lock} />
                        </span>
                    </Tooltip>
                )}
            </div>
            {removeOption && (
                <span
                    onClick={removeOption}
                    style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}
                >
                    <Icon icon={IconCollection.deleteOutline} />
                </span>
            )}
        </div>
    );
};

interface MenuListProps {
    options: any;
    maxHeight: number;
    hasMoreData?: boolean;
    loadMore?: () => void;
    isDataLoading?: boolean;
    hasInfiniteLoader?: boolean;
    currentScrollPosition?: number;
    setCurrentScrollPosition?: (position: number) => void;
    isSelectedOptionHidden?: boolean;
    selectedValue?: any;
}

type FlattenedItem =
    | { type: "group"; element: React.ReactNode }
    | { type: "option"; element: React.ReactNode };

export const MenuList = (props: PropsWithChildren<MenuListProps>) => {
    const {
        options: optionsList,
        children = [],
        maxHeight,
        loadMore,
        hasMoreData,
        isDataLoading,
        hasInfiniteLoader,
        currentScrollPosition,
        setCurrentScrollPosition,
        isSelectedOptionHidden,
        selectedValue,
    } = props;
    const defaultItemHeight = 41.3;
    const defaultGroupHeaderHeight = 28;

    const listRef = useRef<any>(null);
    const rowHeights = useRef<Record<number, number>>({});
    const resizeObservers = useRef<Record<number, ResizeObserver>>({});

    const totalItemsCount = useMemo(() => {
        const optionsCount = optionsList.reduce((acc, group) => {
            return acc + 1 + (group.options?.length || 0);
        }, 0);
        if (isSelectedOptionHidden) {
            return optionsCount ? optionsCount - 1 : 0;
        }
        return optionsCount;
    }, [optionsList, isSelectedOptionHidden]);

    const options = useMemo(() => {
        if (isSelectedOptionHidden && selectedValue) {
            return optionsList.filter((option) => option.value !== selectedValue.value);
        }
        return optionsList;
    }, [optionsList, selectedValue, isSelectedOptionHidden]);

    const { isItemLoaded, displayItemCount, loadMoreItems } = useInfiniteScroll({
        hasMoreData,
        totalItemsCount,
        isDataLoading,
        loadMore,
        listRef,
        currentScrollPosition,
        setCurrentScrollPosition,
    });

    const flattened: FlattenedItem[] = useMemo(() => {
        const childGroups = Children.toArray(children) as any[];

        // no groups, just options
        if (!Array.isArray((childGroups as any)[0]?.props?.children)) {
            const flatChildren =
                isSelectedOptionHidden && selectedValue
                    ? childGroups.filter(
                          (el: any) => el?.props?.data?.value !== selectedValue.value
                      )
                    : childGroups;
            return flatChildren.map((el) => ({
                type: "option",
                element: el,
            }));
        }

        // groups and options
        const flattened = options.flatMap((group: any, groupIndex) => {
            const currentGroup = childGroups[groupIndex];

            const groupHeader = {
                type: "group",
                element: (
                    <div key={`group-${groupIndex}`} className="react-select__group">
                        <GroupLabel label={group.label} />
                    </div>
                ),
            };

            const optionComponents = Children.toArray((currentGroup as any)?.props.children).map(
                (optionComponent, optionIdx) => ({
                    type: "option",
                    element: (
                        <div key={`group-${groupIndex}-option-${optionIdx}`}>{optionComponent}</div>
                    ),
                })
            );

            return [groupHeader, ...optionComponents];
        });

        return flattened;
    }, [children, options, isSelectedOptionHidden, selectedValue]);

    const getRowHeight = useCallback(
        (index: number) => {
            if (index >= flattened.length) {
                return defaultItemHeight;
            }
            return (
                rowHeights.current[index] ??
                (flattened[index].type === "group" ? defaultGroupHeaderHeight : defaultItemHeight)
            );
        },
        [flattened]
    );

    const computeContentHeight = useCallback(() => {
        let sum = 0;
        const count = hasInfiniteLoader ? displayItemCount : totalItemsCount;
        for (let i = 0; i < count; i++) {
            sum += getRowHeight(i);
        }
        return sum;
    }, [hasInfiniteLoader, displayItemCount, totalItemsCount, getRowHeight]);

    const [contentHeight, setContentHeight] = useState(computeContentHeight);

    useEffect(() => {
        rowHeights.current = {};
        setContentHeight(computeContentHeight());
    }, [computeContentHeight, totalItemsCount, displayItemCount, hasInfiniteLoader]);

    const updateHeight = useCallback(
        (index: number, height: number) => {
            if (height !== rowHeights.current[index]) {
                rowHeights.current[index] = height;
                if (listRef.current) {
                    listRef.current.resetAfterIndex(index);
                }
                setContentHeight(computeContentHeight());
            }
        },
        [computeContentHeight]
    );

    const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
        const rowRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (!rowRef.current) return;

            const measure = () => {
                const height = rowRef.current?.scrollHeight;
                if (height) updateHeight(index, height);
            };
            const debounced = debounce(measure, 50);
            const rowObserver = new ResizeObserver(debounced);

            rowObserver.observe(rowRef.current);
            measure();

            resizeObservers.current[index] = rowObserver;

            return () => {
                debounced.cancel();
                rowObserver.disconnect();
                delete resizeObservers.current[index];
            };
        }, [index]);

        return (
            <div ref={rowRef} style={style}>
                {isItemLoaded(index) ? flattened[index].element : <div>Loading...</div>}
            </div>
        );
    };

    const viewportHeight = Math.min(Math.max(contentHeight, defaultItemHeight), maxHeight);

    const listProps = {
        height: viewportHeight,
        width: "100%",
        itemSize: getRowHeight,
    };

    if (totalItemsCount === 0) {
        if (isDataLoading) {
            return (
                <div style={{ maxHeight }}>
                    <span className="loadingMessage">Loading...</span>
                </div>
            );
        } else {
            return (
                <div style={{ maxHeight }}>
                    <NoOptionsMessage />
                </div>
            );
        }
    }

    if (hasInfiniteLoader) {
        return (
            <InfiniteLoader
                isItemLoaded={isItemLoaded}
                itemCount={displayItemCount}
                loadMoreItems={loadMoreItems}
                threshold={3} // Start loading when 3 items away from the end
                minimumBatchSize={10}
            >
                {({ onItemsRendered, ref }) => (
                    <List
                        ref={(el) => {
                            listRef.current = el;
                            ref(el);
                        }}
                        onItemsRendered={onItemsRendered}
                        itemCount={displayItemCount}
                        {...listProps}
                    >
                        {Row}
                    </List>
                )}
            </InfiniteLoader>
        );
    }

    return (
        <List ref={listRef} itemCount={totalItemsCount} {...listProps}>
            {Row}
        </List>
    );
};
